/**
 * The artifacts a collaboration produced, as a list (task 828).
 *
 * One lens: the account's whole output. The per-service narrowing this once
 * offered went away with the AI Service's Artifacts tab (task 854) — an artifact
 * belongs to the Collaboration that produced it, not to a service — so the
 * listing is asked for unfiltered and the /artifacts page is its one caller.
 *
 * A row shows the LATEST version, because that is what "download this" means to
 * a reader; every other version is one click away in the history panel. The
 * service sends that latest version's metadata inside the row for exactly this
 * reason, so the table costs one request no matter how many artifacts it holds.
 */
import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useOptionalToast } from '@/contexts/ToastContext';
import { useArtifacts, useDeleteArtifact, useDownloadArtifactVersion } from '@/hooks/useArtifacts';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  TableHeader,
} from '@/components/ui/DataTable';
import { ArtifactKindBadge } from './ArtifactKindBadge';
import { ArtifactVersionHistoryPanel } from './ArtifactVersionHistoryPanel';
import { formatArtifactSize } from '@/types/artifacts';
import type { Artifact } from '@/types/artifacts';
import DeleteResourceControl from '@/components/shared/DeleteResourceControl';

interface ArtifactsListProps {
  className?: string;
}

/**
 * The empty state does the teaching. An account that has never run an
 * artifact-producing collaboration will reach this page before it reaches an
 * artifact, and "no rows" alone would read as a broken screen rather than a
 * feature not yet used.
 */
const EMPTY_COPY =
  'Artifacts are files produced by your collaborations — documents, exports, and datasets appear here.';

export function ArtifactsList({ className }: ArtifactsListProps) {
  const { data, isLoading, isError } = useArtifacts();
  const download = useDownloadArtifactVersion();
  const deleteArtifact = useDeleteArtifact();
  const toast = useOptionalToast();

  const [historyFor, setHistoryFor] = useState<Artifact | null>(null);

  const artifacts = data?.artifacts ?? [];

  const handleDownload = (artifact: Artifact, event: React.MouseEvent) => {
    event.stopPropagation();
    const versionNumber = artifact.latestVersion?.versionNumber ?? artifact.currentVersionNumber;
    download.mutate(
      { artifactId: artifact.id, versionNumber, filename: artifact.name },
      { onError: () => toast?.error('Could not download', 'That file could not be read.') }
    );
  };

  if (isError) {
    return (
      <p data-testid="artifacts-error" className="text-sm text-red-600 dark:text-red-400">
        Artifacts could not be loaded. Please try again.
      </p>
    );
  }

  if (isLoading) {
    return (
      <p data-testid="artifacts-loading" className="text-sm text-gray-500 dark:text-gray-400">
        Loading artifacts…
      </p>
    );
  }

  if (artifacts.length === 0) {
    return (
      <p
        data-testid="artifacts-empty"
        className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
      >
        {EMPTY_COPY}
      </p>
    );
  }

  return (
    <div className={className} data-testid="artifacts-list">
      <DataTable>
        <DataTableHead>
          <TableHeader className="w-1/3 min-w-0">Name</TableHeader>
          <TableHeader className="w-28">Kind</TableHeader>
          <TableHeader className="w-40">Type</TableHeader>
          <TableHeader className="w-20">Versions</TableHeader>
          <TableHeader className="w-24">Size</TableHeader>
          <TableHeader className="w-32">Created</TableHeader>
          <TableHeader className="w-28">
            <span className="sr-only">Actions</span>
          </TableHeader>
        </DataTableHead>
        <DataTableBody>
          {artifacts.map((artifact) => (
            <DataTableRow
              key={artifact.id}
              // The row opens the history, because the one thing a list cannot
              // show is what came before what it is showing.
              onClick={() => setHistoryFor(artifact)}
            >
              <DataTableCell>
                {/* The identity marker sits on the name rather than the row:
                    `DataTableRow` takes no data attributes, and widening a
                    primitive with fifty call sites to label one table would be
                    a larger change than this table is worth. Per-row controls
                    are reachable by their accessible names, which carry the
                    artifact's own name. */}
                <span
                  data-testid="artifact-name"
                  data-artifact-id={artifact.id}
                  className="block truncate text-sm font-medium text-gray-900 dark:text-white"
                >
                  {artifact.name}
                </span>
              </DataTableCell>
              <DataTableCell>
                <ArtifactKindBadge kind={artifact.kind} />
              </DataTableCell>
              <DataTableCell className="font-mono text-xs text-gray-500 dark:text-gray-400">
                {artifact.contentType}
              </DataTableCell>
              <DataTableCell className="text-sm text-gray-500 dark:text-gray-400">
                <span data-testid="artifact-version-count">{artifact.currentVersionNumber}</span>
              </DataTableCell>
              <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                <span data-testid="artifact-size">
                  {artifact.latestVersion
                    ? formatArtifactSize(artifact.latestVersion.sizeBytes)
                    : '—'}
                </span>
              </DataTableCell>
              <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {new Date(artifact.createdAt).toLocaleDateString()}
              </DataTableCell>
              <DataTableCell className="whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={(event) => handleDownload(artifact, event)}
                    disabled={download.isPending}
                    aria-label={`Download ${artifact.name}`}
                    data-testid="artifact-download"
                    className="text-gray-400 hover:text-indigo-600 disabled:opacity-50 dark:text-gray-500 dark:hover:text-indigo-400"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                  {/* The one delete affordance (task 864). The in-row
                      Confirm/Cancel swap this replaces stated nothing about what
                      an artifact's delete keeps — its versions — and put Confirm
                      exactly where the trash icon had just been. */}
                  <DeleteResourceControl
                    flag="innovate.fe.artifacts.delete"
                    entityLabel="artifact"
                    name={artifact.name}
                    id={artifact.id}
                    stopPropagation
                    testId="artifact-delete"
                    body={
                      <p>
                        It stops appearing in every listing. Its earlier versions are kept with it,
                        so a restore brings the whole history back.
                      </p>
                    }
                    onDelete={() => deleteArtifact.mutateAsync(artifact.id)}
                  />
                </div>
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>

      <ArtifactVersionHistoryPanel
        open={!!historyFor}
        onClose={() => setHistoryFor(null)}
        artifact={historyFor}
      />
    </div>
  );
}

export default ArtifactsList;
